// Stage ④ 실적 원장 — ops-britzmedi margin/SalesLedger.tsx 를 contract_sales
// 테이블 기반으로 개작 (FR-10). 확정 실적 행 + "확정 대기" 완료 딜 프리필.
// 원칙(D5): 달성률은 확정 행만 집계 — 완료 딜은 후보로 제안될 뿐 자동 집계 안 됨.
// 수량·매출은 딜에서 프리필, 환율·원가·수수료 실지급은 사람이 확정 (원본 장표 충실).
import { useEffect, useMemo, useRef, useState } from "react";
import { contractSalesRepo, dealsRepo, type NewContractSale } from "@/lib/db";
import type { ContractRow, ContractSale, Deal } from "@/types";
import { commissionUsd, krw, pct, saleCalc, usd } from "@/lib/margin/model";
import { saleToModel } from "./convert";
import { btn, neg, NumInput, TD, TH, TextInput } from "./ui";

export default function ContractLedger({
  contract,
  sales,
  deals,
  onRefresh,
}: {
  contract: ContractRow;
  sales: ContractSale[];
  /** 이 계약에 연결된 딜 전체 (확정 대기 판정용). */
  deals: Deal[];
  onRefresh: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<ContractSale[]>(sales);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dirty = useRef(new Map<string, Partial<NewContractSale>>());
  const timer = useRef<number | null>(null);

  useEffect(() => setRows(sales), [sales]);

  /** 로컬 수정 + 디바운스 저장 (행 단위). */
  const patchRow = (id: string, up: Partial<NewContractSale>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? ({ ...r, ...up } as ContractSale) : r)));
    dirty.current.set(id, { ...(dirty.current.get(id) ?? {}), ...up });
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const batch = [...dirty.current.entries()];
      dirty.current.clear();
      void Promise.all(batch.map(([rid, patch]) => contractSalesRepo.update(rid, patch)))
        .then(() => onRefresh())
        .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }, 900);
  };

  const removeRow = (id: string) => {
    if (!window.confirm("이 실적 행을 삭제할까요?")) return;
    contractSalesRepo
      .remove(id)
      .then(onRefresh)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  const snap = contract.sim_snapshot;

  const addManualRow = () => {
    const row: NewContractSale = {
      contract_id: contract.id,
      deal_id: null,
      sale_date: new Date().toISOString().slice(0, 10),
      qty: 1,
      price_usd: 0,
      fx: snap?.fx ?? 1_500,
      cost_krw: snap?.cost_krw ?? 0,
      comm_krw: 0,
      extra_krw: 0,
      buyer: contract.buyer_name ?? "",
      note: "",
    };
    contractSalesRepo
      .create(row)
      .then(onRefresh)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  // ── 확정 대기: 완료(done) 딜 중 아직 실적 행이 없는 것 ──
  const confirmedDealIds = useMemo(() => new Set(rows.map((r) => r.deal_id).filter(Boolean)), [rows]);
  const pendingDeals = deals.filter((d) => d.status === "done" && !confirmedDealIds.has(d.id));

  async function confirmDeal(deal: Deal) {
    setBusy(deal.id);
    setError(null);
    try {
      const lines = await dealsRepo.lineItems(deal.id);
      const productLines = lines.filter((l) => l.item_kind === "product" && !l.is_no_commercial_value);
      const qty = productLines.reduce((s, l) => s + (l.quantity || 0), 0);
      const amount = productLines.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price || 0), 0);
      const price = qty > 0 ? amount / qty : 0;
      // 수수료 제안 = 계약 등록 시점 조건(스냅샷)으로 계산 — 실지급으로 반드시 확정할 것.
      const commSuggest =
        snap != null ? qty * commissionUsd(price, { rate: snap.rate, floorCompany: snap.floor_company }) * snap.fx : 0;
      await contractSalesRepo.create({
        contract_id: contract.id,
        deal_id: deal.id,
        sale_date: new Date().toISOString().slice(0, 10),
        qty,
        price_usd: Math.round(price * 100) / 100,
        fx: snap?.fx ?? 1_500,
        cost_krw: snap?.cost_krw ?? 0,
        comm_krw: Math.round(commSuggest),
        extra_krw: 0,
        buyer: contract.buyer_name ?? "",
        note: `딜 ${deal.deal_no}에서 확정`,
      });
      onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const total = rows.reduce(
    (acc, r) => {
      const c = saleCalc(saleToModel(r));
      acc.qty += r.qty || 0;
      acc.revUsd += c.revenueUsd;
      acc.revKrw += c.revenueKrw;
      acc.cost += c.costTotal;
      acc.margin += c.marginKrw;
      return acc;
    },
    { qty: 0, revUsd: 0, revKrw: 0, cost: 0, margin: 0 },
  );

  const dealNoById = useMemo(() => new Map(deals.map((d) => [d.id, d.deal_no])), [deals]);

  return (
    <div className="space-y-4">
      {error && <p className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{error}</p>}

      {/* 확정 대기 — 완료 딜 → 실적 프리필 */}
      {pendingDeals.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <p className="mb-1 text-xs font-bold text-amber-800">
            실적 확정 대기 {pendingDeals.length}건 — 완료된 선적 딜을 실적 원장에 반영하세요
          </p>
          <div className="space-y-1">
            {pendingDeals.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-xs text-slate-700">
                <code className="rounded bg-white px-1">{d.deal_no}</code>
                <span>{d.title ?? "—"}</span>
                <button onClick={() => void confirmDeal(d)} disabled={busy === d.id} className={btn + " ml-auto border-amber-400 text-amber-800 hover:bg-amber-100"}>
                  {busy === d.id ? "확정 중…" : "실적으로 확정 (수량·단가 프리필)"}
                </button>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-amber-700">
            수량·단가는 딜 라인에서 프리필됩니다. 환율·제조원가·수수료는 제안값이니 실지급 기준으로 확정 입력하세요.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-slate-800">■ {contract.name} — 실적 원장 (판매내역·비용·마진)</h3>
        <button onClick={addManualRow} className={btn}>
          + 수동 실적 행 추가
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["출고일", "출처 딜", "바이어", "수량", "단가 USD", "환율", "매출 USD", "매출 KRW", "제조원가/대 KRW", "수수료 KRW", "운임·기타 KRW", "총비용 KRW", "마진 KRW", "마진율", "메모", ""].map((h, i) => (
                <th key={i} className={TH}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={16} className="border border-slate-200 px-3 py-4 text-sm text-slate-400">
                  실적이 없습니다. 완료 딜을 확정하거나 "수동 실적 행 추가"로 입력하세요.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const c = saleCalc(saleToModel(r));
              return (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="border border-slate-200 px-1 py-1">
                    <input
                      type="date"
                      value={r.sale_date}
                      onChange={(e) => patchRow(r.id, { sale_date: e.target.value })}
                      className="rounded border border-slate-300 px-1 py-0.5 text-sm"
                    />
                  </td>
                  <td className="border border-slate-200 px-1 py-1 text-xs">
                    {r.deal_id ? <code>{dealNoById.get(r.deal_id) ?? r.deal_id.slice(0, 8)}</code> : <span className="text-slate-400">수동</span>}
                  </td>
                  <td className="border border-slate-200 px-1 py-1">
                    <TextInput value={r.buyer} onChange={(v) => patchRow(r.id, { buyer: v })} className="w-32" placeholder="바이어" />
                  </td>
                  <td className="border border-slate-200 px-1 py-1">
                    <NumInput value={r.qty} onChange={(v) => patchRow(r.id, { qty: v })} className="w-14 border-amber-300 bg-amber-50" />
                  </td>
                  <td className="border border-slate-200 px-1 py-1">
                    <NumInput value={Number(r.price_usd)} step={500} onChange={(v) => patchRow(r.id, { price_usd: v })} className="w-20 border-amber-300 bg-amber-50" />
                  </td>
                  <td className="border border-slate-200 px-1 py-1">
                    <NumInput value={Number(r.fx)} step={10} onChange={(v) => patchRow(r.id, { fx: v })} className="w-16" />
                  </td>
                  <td className={TD}>{usd(c.revenueUsd)}</td>
                  <td className={TD}>{krw(c.revenueKrw)}</td>
                  <td className="border border-slate-200 px-1 py-1">
                    <NumInput value={Number(r.cost_krw)} step={100_000} onChange={(v) => patchRow(r.id, { cost_krw: v })} className="w-24" />
                  </td>
                  <td className="border border-slate-200 px-1 py-1">
                    <NumInput value={Number(r.comm_krw)} step={100_000} onChange={(v) => patchRow(r.id, { comm_krw: v })} className="w-24" />
                  </td>
                  <td className="border border-slate-200 px-1 py-1">
                    <NumInput value={Number(r.extra_krw)} step={100_000} onChange={(v) => patchRow(r.id, { extra_krw: v })} className="w-24" />
                  </td>
                  <td className={TD}>{krw(c.costTotal)}</td>
                  <td className={TD + " font-bold" + neg(c.marginKrw)}>{krw(c.marginKrw)}</td>
                  <td className={TD}>{pct(c.marginPct)}</td>
                  <td className="border border-slate-200 px-1 py-1">
                    <TextInput value={r.note} onChange={(v) => patchRow(r.id, { note: v })} className="w-28" />
                  </td>
                  <td className="border border-slate-200 px-1 text-center">
                    <button onClick={() => removeRow(r.id)} className="text-xs text-slate-400 hover:text-red-600">
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-50 font-semibold">
                <td className="border border-slate-300 px-2 py-1.5 text-sm" colSpan={3}>
                  합계
                </td>
                <td className={TD + " font-bold"}>{usd(total.qty)}</td>
                <td className={TD} colSpan={2}></td>
                <td className={TD + " font-bold"}>{usd(total.revUsd)}</td>
                <td className={TD + " font-bold"}>{krw(total.revKrw)}</td>
                <td className={TD} colSpan={3}></td>
                <td className={TD + " font-bold"}>{krw(total.cost)}</td>
                <td className={TD + " font-bold" + neg(total.margin)}>{krw(total.margin)}</td>
                <td className={TD + " font-bold"}>{pct(total.revKrw > 0 ? total.margin / total.revKrw : NaN)}</td>
                <td className={TD} colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-[11px] text-slate-400">
        수수료·운임·기타는 건별 실지급 총액(KRW)을 입력합니다. 달성률·페이스는 이 원장의 확정 행만 집계합니다(완료 딜 자동
        집계 아님). 마진 = 매출(KRW) − (제조원가×수량 + 수수료 + 운임·기타).
      </p>
    </div>
  );
}
